// Panels: Basis-Ausbau, Auftraege, Booster, Tooltip und Dialoge.
//
// Jede Liste bildet einen **Aenderungsschluessel** aus dem Zustand und
// ueberspringt den Neubau, wenn er gleich bleibt. Das ist keine
// Mikrooptimierung: ein Voll-Rerender je Frame zerstoert Hover, Fokus und
// Scrollposition — sichtbar als „die Liste flackert und mein Mauszeiger
// verliert den Knopf".
//
// Wer eine neue Liste ergaenzt, ergaenzt auch ihren Schluessel. Wer ein Feld in
// die Darstellung aufnimmt, das der Schluessel nicht erfasst, baut einen Fehler
// ein, der aussieht wie „aktualisiert manchmal nicht".

import { bus, type PanelTab } from '../core/bus';
import { distStr, escapeHtml, fmt, timeStr } from '../core/format';
import { clamp } from '../core/math';
import {
  balancing, booster, boosters, build, builds, fleetOrder, nodes, perks, probeLogs, quests,
  recipe as recipeById, resource, sideQuests, statCounters, story, zoneAtDist, zonesOfOre,
} from '../data/balancing';
import { courseLock } from '../sim/navigation';
import { findsHtml, findsListKey, findsRevealed, type FindsProgress } from './finds';
import { sideClaimed, sideDone, sideStepsDone, sideUnlocked } from '../sim/sidequests';
import type { Build, Goal, StatCounter, TreeNode } from '../data/types';
import type { RankingBoard, RankingPayload, RankingSpan } from '../net/api';
import { RANK_ATTR, RANK_RELOAD_MS, rankTabsHtml, rankingBodyHtml } from './ranking';
import { drawIcon, resIconSvg } from '../gfx/icons';
import { NODE_STATE_ORDER, NODE_STYLE, cssColor } from '../gfx/palette';
import { HELP_PAGES, WIPE_ATTR, helpPageHtml, helpTabsHtml } from './help';
import { isHole, shuffled, slide, solved } from './puzzle';
import { boosterListKey, buildListKey, questListKey } from './listkeys';
import { openRecipes, recipeListKey } from './recipes';
import { hideTooltip, showTooltip } from './tooltip';
import {
  MAX_CONVERT_RUNS, affordableRuns, builtIds, effectPreview, missingReasons, nodeCost,
} from '../sim/stats';
import { buildLocked, buildState, countStates, treeStatus } from '../sim/tree';
import { store } from '../state/store';

const el = (id: string): HTMLElement => {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Panel-Element fehlt: ${id}`);
  return found;
};

export interface PanelActions {
  buyNode(id: string): void;
  build(id: string): void;
  claimQuest(id: string): void;
  /** Einen erfüllten Nebenauftrag abholen. */
  claimSide(id: string): void;
  /** Kurs auf den nächsten offenen Schritt eines Nebenauftrags. */
  setCourse(id: string): void;
  buyBooster(id: string): void;
  /** Den Booster-Schalter setzen: stapeln statt verlaengern. */
  boosterMode(on: boolean): void;
  spendPoint(id: string): void;
  /** Umwandeln — `times` ist eine Absicht, geklemmt wird auf das Machbare. */
  convert(id: string, times: number): void;
  toggleOre(id: string): void;
  /** Das Wunscherz der Drohnen setzen — leere ID heisst „keins". */
  setWishOre(id: string): void;
  /** Der Heimflug — ein bezahlter Zug mit sichtbarem Preis (`GAME-001`). */
  homeJump(): void;
  /**
   * Die Bestenliste holen. `null` heisst „das Netz war es nicht".
   *
   * Sie kommt ueber die Verdrahtung wie jede andere Aktion, damit `ui` den
   * REST-Client nicht selbst haelt — und damit das `catch` um das Netz dort
   * bleibt, wo es hingehoert (Gate FG-13).
   */
  ranking(board: RankingBoard, span: RankingSpan): Promise<RankingPayload | null>;
  wipe(): void;
  setPaused(paused: boolean): void;
  treeViewport(): HTMLElement;
  mapViewport(): HTMLElement;
}

export interface ModalAction {
  label: string;
  dim?: boolean;
  fn?: () => void;
  /**
   * Laeuft **vor** dem Schliessen, mit dem noch bestueckten Dialogkoerper.
   *
   * Der einzige Weg, Eingabefelder auszulesen: `fn` laeuft nach `hideModal`,
   * und ein Formular, das seine Werte aus einem geschlossenen Dialog holt,
   * ist ein Fehler, der erst bei der zweiten Aenderung auffaellt.
   */
  submit?: (body: HTMLElement) => void;
}

/**
 * Wo dieses Erz faellt — die Zeile unter jedem Filterchip.
 *
 * Sie steht als **Text am Chip** und nicht als Tooltip: ein Tipp auf einen
 * Filterchip schaltet bereits den Filter, und eine Bedienung, die nur ueber
 * `hover` erreichbar ist, gibt es am Handy nicht (siehe `oberflaeche.md`).
 *
 * Die Ableitung ist dieselbe wie im HUD und auf der Feldseite der Hilfe
 * (`zonesOfOre`) — eine zweite saehe irgendwann andere Zonen.
 */
function oreWhere(id: string): string {
  const zonen = zonesOfOre(id, store.save.era);
  if (zonen.length === 0) return 'aus dem Konverter';
  return escapeHtml(zonen.map((zone) => zone.name).join(' · '));
}

/** Eine Zahl der Statistik im Format ihrer Art. */
function statValue(kind: StatCounter['kind'], value: number): string {
  if (kind === 'seconds') return timeStr(value);
  if (kind === 'dist') return distStr(value);
  if (kind === 'count') return fmt(Math.round(value));
  return fmt(value);
}

/**
 * Die Zeile des Flottenauftrags — **einmal**, für Aufbau und Nachschreiben.
 *
 * Sie stand zweimal da: als Template im Markup und als Zuweisung im
 * Nachschreiber. Wer nur eine der beiden ändert, sieht seine Änderung genau ein
 * Bild lang, dann schreibt die andere sie weg — eine stille Abweichung
 * derselben Sorte, gegen die `oberflaeche.md` sonst argumentiert.
 */
function fleetLine(progress: number, goal: number, buffLeft: number): string {
  return `Gemeinsam im Feld: ${fmt(Math.min(progress, goal))} / ${fmt(goal)}`
    + (buffLeft > 0 ? ` · Feld-Buff aktiv ${timeStr(buffLeft)}` : '');
}

/**
 * Die Breite eines Fortschrittsbalkens setzen — nur bei echter Änderung.
 *
 * Der Vergleich ist kein Geiz: ein `style.width`, das je Bild neu zugewiesen
 * wird, lässt den Browser das Layout neu rechnen, auch wenn der Wert derselbe
 * ist. Und der Balken steht in einer Liste, die genau darum nicht mehr neu
 * gebaut wird (Issue #80).
 */
function setBar(bar: HTMLElement | null, progress: number, goal: number): void {
  if (!bar) return;
  const breite = `${clamp(progress / goal, 0, 1) * 100}%`;
  if (bar.style.width !== breite) bar.style.width = breite;
}

/** Zahl und Balken eines Auftragsfortschritts nachschreiben. */
function setLive(
  text: HTMLElement,
  bar: HTMLElement | null,
  progress: number,
  goal: number,
): void {
  const zahl = fmt(Math.min(progress, goal));
  if (text.textContent !== zahl) text.textContent = zahl;
  setBar(bar, progress, goal);
}

/** Die Zeilen einer Aufschluesselung: Name und Icon je Schluessel. */
function statBreakdown(per: StatCounter['per']): Array<{ id: string; name: string; icon: string }> {
  if (per === 'res') {
    return balancing().resources.map((entry) => ({
      id: entry.id, name: entry.name, icon: resIconSvg(entry),
    }));
  }
  if (per === 'rockKind') {
    return [...balancing().rockKinds, balancing().richKind].map((kind) => ({
      id: kind.id, name: kind.name, icon: '',
    }));
  }
  if (per === 'channel') {
    return balancing().weaponChannels.map((entry) => ({ id: entry.id, name: entry.name, icon: '' }));
  }
  if (per === 'enemy') {
    return balancing().enemies.map((entry) => ({ id: entry.id, name: entry.name, icon: '' }));
  }
  return [];
}

export class Panels {
  readonly root = el('panel');
  private readonly buildList = el('build-list');
  private readonly questList = el('quest-list');
  private readonly boosterList = el('booster-list');
  private readonly statList = el('stat-list');
  private readonly rankTabs = el('ranking-tabs');
  private readonly rankList = el('ranking-list');
  private readonly perkList = el('perk-list');
  private readonly recipeList = el('recipe-list');
  private readonly oreFilter = el('ore-filter');
  private readonly logList = el('log-list');
  private readonly findsList = el('finds-list');
  /**
   * Der Reiter selbst — **einmal** aufgelöst, nicht je Bild gesucht.
   *
   * `render()` läuft im `requestAnimationFrame`-Takt; ein `querySelector` darin
   * ist genau das, wogegen „DOM-Referenzen einmal auflösen" steht. Und sobald er
   * einmal sichtbar ist, fragt niemand mehr — die Spur wird nicht wieder
   * unbekannt.
   */
  private readonly findsTab = document.querySelector<HTMLElement>('.tab[data-tab="finds"]');
  private findsShown = false;
  private readonly helpTabs = el('help-tabs');
  private readonly helpBody = el('help-body');
  private readonly treeLegend = el('tree-legend');
  private readonly mapLegend = el('map-legend');
  private readonly modal = el('modal');

  open = false;
  tab: PanelTab = 'upgrades';

  private buildKey = '';
  private recipeKey = '';
  private oreKey = '';
  private questKey = '';
  private boosterKey = '';
  private statKey = '';
  private rankKey = '';
  private perkKey = '';
  private logKey = '';
  private findsKey = '';
  private legendKey = '';
  private mapFootKey = '';
  private helpKey = '';

  /**
   * Die Knoten der **laufenden** Zahlen — beim Neubau eingesammelt.
   *
   * Countdown und Fortschritt aendern sich staendig, ihre Liste aber nicht: sie
   * gehoeren darum nicht in den Aenderungsschluessel, sondern werden an Ort und
   * Stelle geschrieben (siehe `listkeys.ts`). Gesammelt wird beim Neubau und
   * nicht je Bild — ein `querySelector` pro Zeile pro Bild ist genau das
   * Anti-Pattern „DOM-Element pro Frame suchen" aus `oberflaeche.md`.
   */
  private boosterLive: { id: string; node: HTMLElement }[] = [];
  private questLive: { goal: Goal; text: HTMLElement; bar: HTMLElement | null }[] = [];
  private fleetLive: { text: HTMLElement; bar: HTMLElement | null } | null = null;

  /**
   * Welche Hilfeseite zuletzt offen war.
   *
   * Ein Feld auf diesem Objekt, wie `tab` selbst — **nicht** im Spielstand
   * (Gate FG-28: ein Persistenzpfad, und der traegt nur, was der Spieler getan
   * hat) und **nicht** im lokalen Zwischenspeicher: dort liegen
   * Geraeteeinstellungen wie Ton und Zoomstufe, die ein Neuladen ueberdauern
   * sollen. Eine zuletzt gelesene Seite ist keine Einstellung.
   */
  private helpPage: string = HELP_PAGES[0].id;

  /**
   * Der Zustand der Bestenliste — **Oberflaeche, kein Fortschritt.**
   *
   * Welche Wertung zuletzt offen war, gehoert weder in den Spielstand
   * (Gate FG-28) noch in den lokalen Zwischenspeicher: das ist dieselbe
   * Ueberlegung wie bei `helpPage`.
   */
  private rankBoard: RankingBoard = 'yield';
  private rankSpan: RankingSpan = 'all';
  private rankData: RankingPayload | null = null;
  private rankStamp = 0;
  /** Laeuft gerade ein Abruf? Ohne diesen Riegel feuert jedes Bild einen neuen. */
  private rankBusy = false;
  private rankFailed = false;

  /** Ist gerade ein Zug beim Server? Siehe `setMoveBusy`. */
  private moveBusy = false;

  constructor(private readonly actions: PanelActions) {
    el('panel-close').onclick = () => this.close();
    el('btn-upgrades').onclick = () => this.toggle('upgrades');
    el('btn-base').onclick = () => this.toggle('base');
    el('btn-help').onclick = () => this.toggle('help');

    document.querySelectorAll<HTMLElement>('.tab').forEach((tab) => {
      tab.onclick = () => this.setTab(tab.dataset.tab as PanelTab);
    });

    bus.on('panel:toggle', ({ tab }) => this.toggle(tab));
    bus.on('panel:close', () => this.close());
    bus.on('move:busy', ({ busy }) => this.setMoveBusy(busy));
  }

  /**
   * Ein laufender Zug ist am Konverter-Knopf zu sehen.
   *
   * Online laesst `net/moves.ts` **einen** Zug zur Zeit zu und verwirft jeden
   * weiteren still. Am Konverter drueckt derselbe Mensch denselben Knopf
   * zwanzigmal — und sah dort nur, dass nichts passiert.
   *
   * Die Marke sitzt am **Container**, nicht an den Knoepfen: der Container
   * ueberlebt jeden Neubau der Liste, sein `innerHTML` nicht. Ein `disabled`
   * je Knopf waere beim naechsten Rerender still wieder weg.
   */
  private setMoveBusy(busy: boolean): void {
    if (this.moveBusy === busy) return;
    this.moveBusy = busy;
    this.recipeList.classList.toggle('busy', busy);
  }

  toggle(tab: PanelTab): void {
    if (this.open && this.tab === tab) this.close();
    else this.show(tab);
  }

  show(tab: PanelTab = 'upgrades'): void {
    this.open = true;
    this.root.classList.remove('hidden');
    this.setTab(tab);
    bus.emit('panel:state', { open: true });
  }

  close(): void {
    // Idempotent, und das ist keine Bequemlichkeit: `panel:close` ist zugleich
    // Befehl (jemand bittet ums Schliessen) und Meldung (es ist geschlossen).
    // Ohne diesen Riegel ruft der eigene Zuhoerer in Zeile 124 `close()` erneut,
    // und die Zustellung laeuft im Kreis — `panel:state` unten wird dann nie
    // gemeldet.
    if (!this.open) return;
    this.open = false;
    this.root.classList.add('hidden');
    hideTooltip();
    bus.emit('panel:close', undefined);
    bus.emit('panel:state', { open: false });
  }

  setTab(tab: PanelTab): void {
    this.tab = tab;
    document.querySelectorAll<HTMLElement>('.tab').forEach((entry) => {
      entry.classList.toggle('active', entry.dataset.tab === tab);
    });
    for (const name of ['upgrades', 'base', 'quests', 'map', 'boosters', 'points', 'stats',
      'ranking', 'log', 'finds', 'colony', 'help']) {
      el('view-' + name).classList.toggle('hidden', name !== tab);
    }
    this.buildKey = ''; this.recipeKey = ''; this.oreKey = ''; this.questKey = ''; this.boosterKey = '';
    this.statKey = ''; this.perkKey = ''; this.logKey = ''; this.legendKey = ''; this.helpKey = '';
    this.mapFootKey = ''; this.rankKey = ''; this.findsKey = '';
    this.render();
  }

  /** Einmal je Frame, wenn das Panel offen ist. */
  render(): void {
    if (!this.open) return;
    if ((store.save.totalRes.crystal ?? 0) > 0) {
      document.querySelector('.tab[data-tab="boosters"]')?.classList.remove('hidden');
    }
    // Der Bestenlisten-Reiter erscheint erst online — wie der Booster-Reiter
    // erst mit dem ersten Kristall. Ein Reiter, der solo nur „keine
    // Verbindung" zeigt, ist ein Versprechen ohne Einlösung. Er bleibt
    // danach stehen: wer die Verbindung verliert, sieht den letzten Stand
    // mit Zeitangabe statt eines verschwundenen Reiters.
    if (!store.authoritative) {
      document.querySelector('.tab[data-tab="ranking"]')?.classList.remove('hidden');
    }
    // Das Fundbuch erscheint erst mit der Spur — wie der Booster-Reiter erst
    // mit dem ersten Kristall. Vorher gäbe es nichts zu zeigen. Danach steht die
    // Frage nicht mehr an: `findsRevealed` allokiert (es filtert die Tabelle),
    // und der Takt läuft je Bild.
    if (!this.findsShown && findsRevealed(this.findsProgress())) {
      this.findsShown = true;
      this.findsTab?.classList.remove('hidden');
    }
    if (this.tab === 'upgrades') this.renderTreeLegend();
    else if (this.tab === 'base') { this.renderBuilds(); this.renderRecipes(); this.renderOreFilter(); }
    // Neubau **und** laufende Zahlen: der Neubau haengt am Schluessel und
    // bleibt darum meist aus, das Nachschreiben laeuft jedes Bild. Genau diese
    // Trennung haelt den Knopf unter dem Finger (Issue #80).
    else if (this.tab === 'quests') { this.renderQuests(); this.refreshQuestLive(); }
    else if (this.tab === 'map') this.renderMapFoot();
    else if (this.tab === 'boosters') { this.renderBoosters(); this.refreshBoosterLive(); }
    else if (this.tab === 'points') this.renderPerks();
    else if (this.tab === 'stats') this.renderStats();
    else if (this.tab === 'ranking') this.renderRanking();
    else if (this.tab === 'log') this.renderLog();
    else if (this.tab === 'finds') this.renderFinds();
    else if (this.tab === 'help') this.renderHelp();
  }

  // ── Hilfe ──────────────────────────────────────────────────────────────────
  //
  // Der Inhalt liegt in `ui/help.ts` als reine Funktionen; hier steht nur die
  // Verdrahtung. Der Aenderungsschluessel nimmt bewusst die **Zone** des
  // groessten Abstands, nicht den Abstand selbst: sonst baute sich die Seite bei
  // jedem geflogenen Meter neu auf, und die Scrollposition waere weg.

  private renderHelp(): void {
    const key = this.helpPage + '|' + store.save.era + '|' +
      zoneAtDist(store.stat('deepest'), store.save.era);
    if (key === this.helpKey) return;
    // Nur ein Seitenwechsel springt nach oben. Das Panel haelt die Simulation
    // **nicht** an — wer beim Lesen weiterdriftet und eine Zonengrenze
    // ueberfliegt, baut die Seite neu auf; ein Sprung an den Anfang saehe dann
    // wie ein Fehler aus.
    const seitenwechsel = !this.helpKey.startsWith(this.helpPage + '|');
    this.helpKey = key;

    this.helpTabs.innerHTML = helpTabsHtml(this.helpPage);
    this.helpBody.innerHTML = helpPageHtml(this.helpPage, {
      deepest: store.stat('deepest'),
      era: store.save.era,
    });
    if (seitenwechsel) this.helpBody.scrollTop = 0;

    for (const button of this.helpTabs.querySelectorAll<HTMLButtonElement>('[data-help]')) {
      button.onclick = () => {
        this.helpPage = button.dataset.help ?? HELP_PAGES[0].id;
        this.helpKey = '';
      };
    }
    const wipe = this.helpBody.querySelector<HTMLButtonElement>(`[${WIPE_ATTR}]`);
    if (wipe) wipe.onclick = () => this.confirmWipe();
  }

  // ── Bestenliste ────────────────────────────────────────────────────────────
  //
  // Der einzige Reiter, dessen Inhalt **nicht** aus dem Spielstand folgt: er
  // kommt über REST und ist damit immer ein Stand von vorhin. Deshalb steht
  // das Alter dabei — eine Liste ohne Zeitangabe sieht bei jedem Wert nach
  // „jetzt" aus, und bei Verbindungsabbruch bliebe sie stumm falsch stehen.
  //
  // Sie ist **keine** vierte Client-Nachricht (Gate FG-32): der WebSocket
  // kennt weiterhin genau drei, und der Takt der Simulation hängt an keiner
  // Rangfolge.

  private renderRanking(): void {
    const alter = performance.now() - this.rankStamp;
    if (!this.rankBusy && (this.rankData === null || alter > RANK_RELOAD_MS)) this.fetchRanking();

    // Der Schlüssel erfasst die Umschalter **und** den Zeitstempel der
    // Antwort: ohne den Stempel bliebe eine neu geladene, inhaltsgleiche
    // Liste mit veraltetem „vor 40 s" stehen.
    const key = [
      this.rankBoard, this.rankSpan, this.rankStamp, this.rankBusy, this.rankFailed,
      Math.floor(alter / 1000),
    ].join('|');
    if (key === this.rankKey) return;
    this.rankKey = key;

    this.rankTabs.innerHTML = rankTabsHtml(this.rankBoard, this.rankSpan);
    this.rankList.innerHTML = rankingBodyHtml({
      data: this.rankData, board: this.rankBoard, failed: this.rankFailed, age: alter,
    });

    for (const button of this.rankTabs.querySelectorAll<HTMLButtonElement>(`[${RANK_ATTR}]`)) {
      button.onclick = () => {
        const wert = button.dataset.rankValue ?? '';
        if (button.dataset.rank === 'board') this.rankBoard = wert as RankingBoard;
        else this.rankSpan = wert as RankingSpan;
        // Ein Umschalter wechselt die Liste, nicht nur ihre Sortierung — die
        // alte stehen zu lassen zeigte fremde Zahlen unter neuer Überschrift.
        this.rankData = null;
        this.rankStamp = 0;
        this.rankFailed = false;
        this.rankKey = '';
      };
    }
  }

  private fetchRanking(): void {
    this.rankBusy = true;
    const board = this.rankBoard;
    const span = this.rankSpan;
    void this.actions.ranking(board, span).then((payload) => {
      this.rankBusy = false;
      // Wer zwischenzeitlich umgeschaltet hat, bekommt nicht die alte
      // Antwort untergeschoben — sie käme sonst unter neuer Überschrift an.
      if (board !== this.rankBoard || span !== this.rankSpan) return;
      this.rankFailed = payload === null;
      if (payload !== null) this.rankData = payload;
      this.rankStamp = performance.now();
    });
  }

  // ── Missionslog ────────────────────────────────────────────────────────────
  //
  // Die Kapitel werden **nicht gespeichert**: sie folgen aus der Statistik und
  // dem Ausbau. Ein zweiter Zustand daneben liefe auseinander. Der Ausloeser ist
  // dieselbe `Goal`-Struktur wie bei den Auftraegen — also dieselbe Auswertung.

  private renderLog(): void {
    const offen = story().filter((chapter) => this.goalProgress(chapter.trigger) >= chapter.trigger.n);
    // Sondenfunde gehoeren in den Aenderungsschluessel: ohne sie erschiene ein
    // frisch geborgenes Log erst beim naechsten Kapitel.
    const funde = probeLogs().filter((log) => store.save.probeLogs[log.id]);
    // Die Ära steht mit im Schlüssel: das Ankunftskapitel löst über sie aus, und
    // ohne sie erschiene es erst beim nächsten gezählten Brocken.
    const key = store.save.era + '§' + offen.map((chapter) => chapter.id).join('|')
      + '§' + funde.map((log) => log.id).join('|');
    if (key === this.logKey) return;
    this.logKey = key;

    const naechstes = story().find((chapter) => this.goalProgress(chapter.trigger) < chapter.trigger.n);
    this.logList.innerHTML = offen
      .map((chapter) => `<div class="chapter"><h3>${escapeHtml(chapter.title)}</h3>` +
        `<p>${escapeHtml(chapter.text)}</p></div>`)
      .join('') + (naechstes
        ? '<div class="chapter locked"><h3>… weiter im Log</h3>' +
          '<p>Flieg raus, bau ab, rüste auf.</p></div>'
        : '') + (funde.length
        ? '<div class="chapter locked"><h3>Sondenfunde</h3>' +
          '<p>Daten-Logs verlassener Sonden aus dem Feld.</p></div>' + funde
          .map((log) => `<div class="chapter probe"><h3>${escapeHtml(log.title)}</h3>` +
            `<p>${escapeHtml(log.text)}</p></div>`)
          .join('')
        : '');
  }

  // ── Fundbuch ───────────────────────────────────────────────────────────────
  //
  // Der Inhalt liegt in `ui/finds.ts` als reine Funktionen; hier steht nur die
  // Verdrahtung — dieselbe Aufteilung wie bei der Hilfe.

  /** Was die Seite liest, an **einer** Stelle zusammengestellt. */
  private findsProgress(): FindsProgress {
    return {
      era: store.save.era,
      caches: store.save.caches,
      cacheReady: store.save.cacheReady,
      gear: store.save.gear,
      deepest: store.stat('deepest'),
      seed: store.fieldSeed,
      built: store.save.built,
      // Dieselbe Karte, die auch der Baum liest — die Entscheidung „wer zaehlt
      // als erlegt" bleibt in `ui/finds.ts`, wo die Waechter-ID herkommt.
      kills: store.statCard('enemiesByKind'),
    };
  }

  private renderFinds(): void {
    const progress = this.findsProgress();
    const key = findsListKey(progress);
    if (key === this.findsKey) return;
    this.findsKey = key;
    this.findsList.innerHTML = findsHtml(progress);
  }

  // ── Kachelschieberätsel ────────────────────────────────────────────────────
  //
  // Es erscheint **nach** dem bezahlten Ausbau und entscheidet nichts: Lösen
  // gibt eine Meldung, Abbrechen kostet keinen Fortschritt. Der Dialog pausiert
  // die Simulation wie jeder andere (VIEW-001).

  showPuzzle(stage: number, name: string, seed: number): void {
    let tiles = shuffled(stage, seed);

    const zeichnen = (): void => {
      const body = el('modal-body');
      body.innerHTML = '<p style="margin-bottom:10px">Die Bauteile sind durcheinander. ' +
        'Schieb sie zurecht — oder lass es, ' + escapeHtml(name) + ' steht ohnehin.</p>' +
        `<div class="puzzle">${tiles.map((tile, index) =>
          isHole(tile)
            ? '<div class="ptile hole"></div>'
            : `<button class="ptile" data-tile="${index}">${tile + 1}</button>`,
        ).join('')}</div>`;

      for (const button of body.querySelectorAll<HTMLButtonElement>('[data-tile]')) {
        button.onclick = () => {
          const next = slide(tiles, Number(button.dataset.tile));
          if (!next) return;
          tiles = next;
          if (solved(tiles)) {
            this.hideModal();
            bus.emit('toast', { text: `${name} ausgerichtet — sauber gebaut`, tone: 'good' });
            return;
          }
          zeichnen();
        };
      }
    };

    this.showModal(`${name} — Feinjustierung`, '', [{ label: 'Später', dim: true }]);
    zeichnen();
  }

  // ── Erzfilter ──────────────────────────────────────────────────────────────
  //
  // Er wirkt auf **jede** Zielsuche, nicht nur auf die Drohnen: „welche Erze
  // abgebaut oder ignoriert werden" ist eine Aussage ueber das ganze Schiff.
  // Der letzte Haken laesst sich nicht loeschen — sonst baute nichts mehr ab.

  private renderOreFilter(): void {
    const sichtbar = balancing().resources.filter(
      (entry) => (store.save.totalRes[entry.id] ?? 0) > 0,
    );
    // Die Ära gehört in den Schlüssel: die Zonenzeile unter jedem Chip ist nach
    // dem Sprung eine andere, und ohne sie bliebe die alte stehen, bis sich
    // zufällig ein Filterhaken ändert.
    const key = sichtbar.map((entry) => entry.id + (store.save.ignoredOres[entry.id] ? '0' : '1')).join('|')
      + '§' + store.save.wishOre + '§' + store.save.era;
    if (key === this.oreKey) return;
    this.oreKey = key;

    if (sichtbar.length < 2) {
      this.oreFilter.innerHTML = '';
      return;
    }

    // **Das Wunscherz steht neben dem Filter, nicht darin.** Der Filter sagt
    // „ob", das Wunscherz „was zuerst" — und es kann nur sein, was der Filter
    // durchlässt: bevorzugt holen und zugleich nicht anfassen wäre ein
    // Widerspruch, den niemand auflöst. Ein ausgefiltertes Erz erscheint hier
    // deshalb gar nicht erst.
    const waehlbar = sichtbar.filter((entry) => !store.save.ignoredOres[entry.id]);
    this.oreFilter.innerHTML = '<h3 class="perk-head">Erzfilter — was abgebaut wird</h3>' +
      sichtbar.map((entry) => {
        const an = !store.save.ignoredOres[entry.id];
        return `<button class="orechip ${an ? 'on' : ''}" data-ore="${entry.id}">` +
          `${resIconSvg(entry, 14)}<span class="otxt">` +
          `<span>${escapeHtml(entry.name)} ${an ? '✓' : '✕'}</span>` +
          `<span class="where">${oreWhere(entry.id)}</span></span></button>`;
      }).join('')
      + '<h3 class="perk-head">Wunscherz — was die Drohnen bevorzugt holen</h3>'
      + `<button class="orechip ${store.save.wishOre ? '' : 'on'}" data-wish="">keins</button>`
      + waehlbar.map((entry) => {
        const gewaehlt = store.save.wishOre === entry.id;
        return `<button class="orechip ${gewaehlt ? 'on' : ''}" data-wish="${entry.id}">` +
          `${resIconSvg(entry, 14)}<span class="otxt">` +
          `<span>${escapeHtml(entry.name)}</span>` +
          `<span class="where">${oreWhere(entry.id)}</span></span></button>`;
      }).join('');

    // **Treffer beim Drücken**, nicht beim Bewegen: ein Finger kennt kein
    // Zeigen, und `onclick` auf einem Chip ist dafür in Ordnung — er ist ein
    // echter Knopf, kein Bereich, den erst ein Hover verrät.
    for (const button of this.oreFilter.querySelectorAll<HTMLButtonElement>('[data-ore]')) {
      button.onclick = () => this.actions.toggleOre(button.dataset.ore ?? '');
    }
    for (const button of this.oreFilter.querySelectorAll<HTMLButtonElement>('[data-wish]')) {
      button.onclick = () => this.actions.setWishOre(button.dataset.wish ?? '');
    }
  }

  // ── Ressourcenkonverter ────────────────────────────────────────────────────
  //
  // Er steht unter dem Ausbau, weil er einer ist: er erscheint erst mit der
  // Ausbaustufe, die ihn traegt.

  private renderRecipes(): void {
    const offen = openRecipes(store.save.built);
    // Der Schluessel bildet ab, **was die Liste zeigt** — welche Rezepte offen
    // sind und welcher Kostenposten gedeckt ist —, nicht woraus sie es
    // rechnet. Vorher stand hier jede gefloorte Ressourcenzahl, und solange
    // die Drohnen graben, aendert sich so eine Zahl mehrmals pro Sekunde: die
    // Liste wurde dauernd neu gebaut, und wer dabei klickte, griff ins Leere
    // (`ui/recipes.ts`).
    const key = recipeListKey(offen, store.save.res);
    if (key === this.recipeKey) return;
    this.recipeKey = key;

    if (!offen.length) {
      this.recipeList.innerHTML = '';
      return;
    }

    this.recipeList.innerHTML = '<h3 class="perk-head">Ressourcenkonverter</h3>' + offen.map((entry) => {
      // **Alle drei Knoepfe teilen einen Zustand**: gedeckt fuer *einen*
      // Durchlauf. `×10` bleibt klickbar, wenn nur sieben gehen — es wird ja
      // geklemmt. Ein eigenes „reicht fuer zehn" waere eine zweite Schwelle im
      // Aenderungsschluessel und damit mehr Neubau ohne Gegenwert.
      const bezahlbar = store.canAfford(entry.inputs);
      const gesperrt = bezahlbar ? '' : 'disabled';
      const kosten = Object.entries(entry.inputs).map(([res, menge]) => {
        const icon = resource(res);
        const genug = (store.save.res[res] ?? 0) >= menge;
        return `<span style="color:${genug ? 'inherit' : '#ff6a5c'}">` +
          `${icon ? resIconSvg(icon, 14) : ''}${fmt(menge)}</span>`;
      }).join(' ');
      const ausgabe = resource(entry.outRes);
      return `<div class="perk ${bezahlbar ? 'ready' : ''}">
        <img class="bi" width="34" height="34" data-icon="${entry.icon}" alt="">
        <div class="ptxt">
          <div class="pname">${escapeHtml(entry.name)}</div>
          <div class="pdesc">${escapeHtml(entry.desc)}</div>
          <div class="pdesc">${kosten} → ${ausgabe ? resIconSvg(ausgabe, 14) : ''}${fmt(entry.outAmount)}</div>
        </div>
        <div class="pbtns">
          <button data-recipe="${entry.id}" data-times="1" ${gesperrt}>×1</button>
          <button data-recipe="${entry.id}" data-times="10" ${gesperrt}>×10</button>
          <button data-recipe="${entry.id}" data-times="max" ${gesperrt}>max</button>
        </div>
      </div>`;
    }).join('');

    this.paintIcons(this.recipeList);
    for (const button of this.recipeList.querySelectorAll<HTMLButtonElement>('[data-recipe]')) {
      button.onclick = () => this.convertClick(button.dataset.recipe ?? '', button.dataset.times);
    }
  }

  /**
   * Ein Konverter-Knopf wurde gedrueckt.
   *
   * `max` wird **hier** aufgeloest und nicht beim Rendern. Stuende die Zahl im
   * Markup, muesste sie in den Aenderungsschluessel — und der waere damit
   * wieder eine Groesse, die sich bei jedem eingebuchten Erzposten aendert.
   * Genau daran hing der Klickverlust, gegen den `ui/recipes.ts` geschrieben
   * ist.
   */
  private convertClick(id: string, times: string | undefined): void {
    const target = recipeById(id);
    if (!target) return;
    const menge = times === 'max'
      ? affordableRuns(store.save.res, target, MAX_CONVERT_RUNS)
      : Number(times);
    if (menge > 0) this.actions.convert(id, menge);
  }

  // ── Level-Punkte ───────────────────────────────────────────────────────────
  //
  // Sie standen einmal ueber der Statistik, mit der Begruendung „derselbe
  // Blick". Das trug nicht: die Statistik ist ein **Nachschlagewerk**, einen
  // Punkt auszugeben ist ein **Zug**. Wer einen Kaufknopf sucht, sucht ihn
  // nicht unter „zerlegte Brocken je Erz" — und die Zaehlerliste fing hinter
  // sechs Kacheln erst nach einem halben Bildschirm an.
  //
  // Der eigene Reiter traegt darum auch ein Badge (`hud.ts`, `updateBadges`):
  // offene Punkte sind der einzige Fortschritt des Spiels, der liegen bleibt,
  // ohne dass irgendwo etwas fehlt.

  private renderPerks(): void {
    const offen = store.save.ship.points;
    const key = offen + '#' + perks().map((entry) => store.save.perks[entry.id] ?? 0).join(',');
    if (key === this.perkKey) return;
    this.perkKey = key;

    const kopf = offen > 0
      ? `<h3 class="perk-head">▲ ${offen} ${offen === 1 ? 'Punkt' : 'Punkte'} zu vergeben</h3>`
      : '<h3 class="perk-head">Keine offenen Punkte — jeder Stufenaufstieg gibt einen</h3>';

    this.perkList.innerHTML = kopf + perks().map((entry) => {
      const stufe = store.save.perks[entry.id] ?? 0;
      const voll = stufe >= entry.max;
      const kaufbar = !voll && offen > 0;
      const wirkung = entry.asPct
        ? `+${Math.round(entry.per * 100)}%`
        : `+${fmt(entry.per)}`;
      return `<div class="perk ${voll ? 'maxed' : kaufbar ? 'ready' : ''}">
        <img class="bi" width="34" height="34" data-icon="${entry.icon}" alt="">
        <div class="ptxt">
          <div class="pname">${escapeHtml(entry.name)} <span class="plevel">${stufe}/${entry.max}</span></div>
          <div class="pdesc">${escapeHtml(entry.desc)} — ${wirkung} je Punkt</div>
        </div>
        <button data-perk="${entry.id}" ${kaufbar ? '' : 'disabled'}>▲</button>
      </div>`;
    }).join('');

    this.paintIcons(this.perkList);
    for (const button of this.perkList.querySelectorAll<HTMLButtonElement>('[data-perk]')) {
      button.onclick = () => this.actions.spendPoint(button.dataset.perk ?? '');
    }
  }

  // ── Statistik ──────────────────────────────────────────────────────────────
  //
  // Die Zaehler und ihre deutschen Namen kommen aus den Tabellen (Gate FG-36),
  // nicht aus dieser Datei: eine zweite Textquelle liefe still auseinander.

  private renderStats(): void {
    const stats = store.save.stats;
    // Der Aenderungsschluessel erfasst **jeden** Zaehler. Fehlte einer, saehe
    // der Fehler aus wie „aktualisiert manchmal nicht".
    const key = statCounters()
      .map((counter) => {
        const value = stats[counter.id];
        return typeof value === 'object'
          ? Object.entries(value).map(([id, n]) => id + Math.floor(n)).join('.')
          : Math.floor(value ?? 0);
      })
      .join('|');
    if (key === this.statKey) return;
    this.statKey = key;

    this.statList.innerHTML = balancing()
      .statGroups.map((group) => {
        const rows = statCounters()
          .filter((counter) => counter.group === group.id)
          .map((counter) => this.statRows(counter))
          .join('');
        return rows ? `<div class="stat-group"><h3>${escapeHtml(group.name)}</h3>${rows}</div>` : '';
      })
      .join('');
  }

  /** Eine Zeile — oder bei aufgeschluesselten Zaehlern eine kleine Tabelle. */
  private statRows(counter: StatCounter): string {
    const value = store.save.stats[counter.id];
    if (!counter.per) {
      return `<div class="stat-row"><span>${escapeHtml(counter.name)}</span>` +
        `<b>${statValue(counter.kind, (value as number) ?? 0)}</b></div>`;
    }

    const card = (value as Record<string, number>) ?? {};
    const parts = statBreakdown(counter.per)
      .map(({ id, name, icon }) => {
        const amount = card[id] ?? 0;
        if (amount <= 0) return '';
        return `<div class="stat-sub">${icon}<span>${escapeHtml(name)}</span>` +
          `<b>${statValue(counter.kind, amount)}</b></div>`;
      })
      .filter(Boolean)
      .join('');
    if (!parts) return '';
    return `<div class="stat-row stat-head"><span>${escapeHtml(counter.name)}</span></div>${parts}`;
  }

  /**
   * Die abgeschlossene Kette eines früheren Akts — **eine** Zeile.
   *
   * Sie verschwindet nicht: „was ich schon gebaut habe" ist der Teil des
   * Fortschritts, den man am längsten erarbeitet hat. Sie steht nur nicht mehr
   * als sechs Zeilen da, die alle dasselbe sagen.
   */
  private pastChainRow(erledigt: Build[]): string {
    if (!erledigt.length) return '';
    const letzte = erledigt[erledigt.length - 1];
    return `<div class="booster built">
      <img class="bi" src="" data-icon="${letzte.icon}" alt="" width="34" height="34">
      <div class="btxt">
        <div class="bname">Randfeld — ${erledigt.length} Stufen ✓</div>
        <div class="bdesc">Von der Werkstatt bis zum ${escapeHtml(letzte.name)}. Steht.</div>
      </div>
      <div class="bdesc">fertig</div>
    </div>`;
  }

  // ── Basis-Ausbau ───────────────────────────────────────────────────────────
  private renderBuilds(): void {
    // Der Schlüssel steht als reine Funktion in `listkeys.ts` und bildet ab,
    // **was die Liste zeigt**: ein Bit je Kostenposten, dazu Stufe,
    // Abschlussmarke und Ära. Vorher endete er auf jeder gefloorten
    // Ressourcenzahl — der Fehler aus #58, unverändert, und ausgerechnet an der
    // Liste mit dem `Bauen`-Knopf: solange die Drohnen graben, wurde sie
    // mehrmals pro Sekunde ersetzt, und ein Klick über einen Neubau hinweg
    // feuerte nie (Issue #80).
    const key = buildListKey(store.save);
    if (key === this.buildKey) return;
    this.buildKey = key;

    // Die Liste zeigt die Kette des **aktuellen** Akts. Alle dreizehn Stufen
    // untereinander wären nach dem Sprung eine Liste, in der die Hälfte für
    // immer abgehakt ist — die abgeschlossene Kette steht deshalb als eine
    // Zeile darüber, nicht als acht.
    const alle = builds();
    const era = store.save.era;
    const erledigt = alle.filter((entry) => entry.minEra < era);
    const kette = alle.filter((entry) => entry.minEra === era);
    const versatz = erledigt.length;

    this.buildList.innerHTML = this.pastChainRow(erledigt)
      + kette.map((entry, nummer) => {
        const index = versatz + nummer;
        const zustand = buildState(store.save, index, entry.minEra);
        const done = zustand === 'done';
        const current = zustand === 'open';
        const cost = Object.entries(entry.cost).map(([res, amount]) => {
          const ok = (store.save.res[res] ?? 0) >= amount;
          const icon = resource(res);
          return `<span style="color:${ok ? '#0b5c1e' : '#8a2318'}">
            ${icon ? resIconSvg(icon, 14) : ''} ${fmt(amount)}</span>`;
        }).join(' ');
        const affordable = current && store.canAfford(entry.cost);
        // Der Hinweis kommt aus **derselben** Auswertung wie die Sperre.
        const grund = buildLocked(zustand);
        return `<div class="booster ${done ? 'built' : ''}" style="${current ? '' : 'opacity:.62'}">
          <img class="bi" src="" data-icon="${entry.icon}" alt="" width="34" height="34">
          <div class="btxt">
            <div class="bname">${nummer + 1}. ${escapeHtml(entry.name)}${done ? ' ✓' : ''}</div>
            <div class="bdesc">${escapeHtml(entry.desc)}</div>
            <div class="bdesc" style="margin-top:3px">⮕ ${escapeHtml(entry.grants)}</div>
            ${done ? '' : `<div class="bdesc" style="margin-top:4px">${cost}</div>`}
          </div>
          ${done
            ? '<div class="bdesc">fertig</div>'
            : current
              ? `<button data-build="${entry.id}" ${affordable ? '' : 'disabled'}>Bauen</button>`
              : `<div class="bdesc">${escapeHtml(grund)}</div>`}
        </div>`;
      }).join('') + this.homeJumpRow();

    this.paintIcons(this.buildList);
    this.buildList.querySelectorAll<HTMLButtonElement>('[data-build]').forEach((button) => {
      button.onclick = () => this.actions.build(button.dataset.build ?? '');
    });
    const sprung = this.buildList.querySelector<HTMLButtonElement>('[data-home]');
    if (sprung) sprung.onclick = () => this.actions.homeJump();
  }

  /**
   * Der Heimflug als **eigene Zeile** — mit sichtbarem Preis.
   *
   * Vorher setzte die letzte Ausbaustufe ihn nebenbei mit; der Sprung fand nur
   * als Text statt. Jetzt ist er eine Entscheidung: der Spieler sieht, was er
   * kostet, und löst ihn selbst aus. Die Zeile hängt am **Ring**, nicht an der
   * Deckung — ein Preis, den man erst sieht, wenn man ihn zahlen kann, ist
   * keiner.
   */
  private homeJumpRow(): string {
    const data = balancing();
    if (!store.save.finished || store.save.era !== data.eraFirst) return '';
    const cost = Object.entries(data.homeJumpCost).map(([res, amount]) => {
      const ok = (store.save.res[res] ?? 0) >= amount;
      const icon = resource(res);
      return `<span style="color:${ok ? '#0b5c1e' : '#8a2318'}">
        ${icon ? resIconSvg(icon, 14) : ''} ${fmt(amount)}</span>`;
    }).join(' ');
    const affordable = store.canAfford(data.homeJumpCost);
    return `<div class="booster">
      <img class="bi" src="" data-icon="nova" alt="" width="34" height="34">
      <div class="btxt">
        <div class="bname">Heimflug</div>
        <div class="bdesc">Der Ring steht. Die Vorräte gehen als Treibstoff mit —
          Schiff, Stufen und alles Gekaufte fliegen unangetastet mit.</div>
        <div class="bdesc" style="margin-top:4px">${cost}</div>
      </div>
      <button data-home="1" ${affordable ? '' : 'disabled'}>Springen</button>
    </div>`;
  }

  // ── Auftraege ──────────────────────────────────────────────────────────────
  private renderQuests(): void {
    // Die Liste steht **ab** der Ära des Auftrags. Nach vorn wirkt der Riegel
    // nicht: die Aufträge aus dem Randfeld bleiben im Heimatsystem gültig und
    // abholbar. Umgekehrt wäre „sammle Bergungsstahl" in einer Welt, in der
    // keiner fällt, kein Ziel, sondern ein Spoiler mit Fortschritt 0 %.
    const offen = quests().filter((entry) => entry.minEra <= store.save.era);
    // Der Schlüssel steht als reine Funktion in `listkeys.ts`. Er erfasst Ära,
    // Ausbaustufe, die Schritte der Nebenaufträge und je Auftrag **ob** er
    // abholbar ist — nicht den laufenden Fortschritt. Zwei zu feine Größen
    // standen vorher darin: der gefloorte Auftragsfortschritt und
    // `ceil(buffLeft)`, das `store.tickFleet` **lokal** herunterzählt. Beides
    // baute die Liste laufend neu und nahm den `Belohnung abholen`-Knopf mit
    // (Issue #80). Die Zahlen schreibt jetzt `refreshQuestLive` nach.
    const key = questListKey(store.save, (id) => this.questProgress(id), store.fleet);
    if (key === this.questKey) return;
    this.questKey = key;

    this.questList.innerHTML = this.fleetHtml() + this.sideHtml() + offen.map((quest) => {
      const progress = this.questProgress(quest.id);
      const target = quest.goal.n;
      const done = progress >= target;
      const claimed = Boolean(store.save.quests[quest.id]);
      const reward = Object.entries(quest.reward).map(([res, amount]) => {
        const icon = resource(res);
        return `${icon ? resIconSvg(icon, 14) : ''}${fmt(amount)}`;
      }).join(' ');
      return `<div class="quest ${claimed ? 'done' : done ? 'claimable' : ''}">
        <div class="quest-top">
          <span class="quest-name">${escapeHtml(quest.name)}${claimed ? ' ✓' : ''}</span>
          <span class="quest-reward">${reward}</span>
        </div>
        <div class="quest-sub">${escapeHtml(goalText(quest.id))} — <span data-quest-prog="${quest.id}">${
          fmt(Math.min(progress, target))}</span> / ${fmt(target)}</div>
        <div class="quest-bar"><div data-quest-bar="${quest.id}" style="width:${
          clamp(progress / target, 0, 1) * 100}%"></div></div>
        ${done && !claimed ? `<button data-claim="${quest.id}">Belohnung abholen</button>` : ''}
      </div>`;
    }).join('');

    this.questList.querySelectorAll<HTMLButtonElement>('[data-claim]').forEach((button) => {
      button.onclick = () => this.actions.claimQuest(button.dataset.claim ?? '');
    });
    this.questList.querySelectorAll<HTMLButtonElement>('[data-side]').forEach((button) => {
      button.onclick = () => this.actions.claimSide(button.dataset.side ?? '');
    });
    // Der Kurs gehört der Weltszene, nicht dem Panel: `ui` und `scenes` kennen
    // sich nicht (Gate FG-04), der Kanal ist die Verdrahtung.
    this.questList.querySelectorAll<HTMLButtonElement>('[data-course]').forEach((button) => {
      button.onclick = () => this.actions.setCourse(button.dataset.course ?? '');
    });
    this.collectQuestLive();
  }

  /**
   * Die Knoten der laufenden Zahlen einsammeln — einmal je Neubau.
   *
   * Mitgenommen wird das **Ziel selbst**, nicht die Auftrags-ID: `questProgress`
   * löst eine ID über `quests().find(…)` auf, also über eine lineare Suche, und
   * das wären je Bild so viele Suchen wie Aufträge (`balancing.md`: „Lesezugriff
   * läuft nie über eine lineare Suche"). Mit dem Ziel in der Hand ist das
   * Nachschreiben ein Durchgang.
   */
  private collectQuestLive(): void {
    const ziele = new Map(quests().map((entry) => [entry.id, entry.goal]));
    this.questLive = [...this.questList.querySelectorAll<HTMLElement>('[data-quest-prog]')]
      .flatMap((text) => {
        const id = text.dataset.questProg ?? '';
        const goal = ziele.get(id);
        if (!goal) return [];
        return [{
          goal,
          text,
          bar: this.questList.querySelector<HTMLElement>(`[data-quest-bar="${id}"]`),
        }];
      });
    const fleetText = this.questList.querySelector<HTMLElement>('[data-fleet-prog]');
    this.fleetLive = fleetText
      ? { text: fleetText, bar: this.questList.querySelector<HTMLElement>('[data-fleet-bar]') }
      : null;
  }

  /**
   * Fortschrittszahlen und Balken — **ohne** Neubau der Liste.
   *
   * Der Fortschritt ist eine laufende Zahl: bei einem Ressourcenziel steigt er
   * mit jeder eingebuchten Einheit. Im Änderungsschlüssel machte er die Liste
   * mehrmals pro Sekunde neu und riss den `Belohnung abholen`-Knopf mit. Ob ein
   * Auftrag **abholbar** ist, bleibt dagegen im Schlüssel — das ist eine
   * Schwelle, und mit ihr erscheint der Knopf.
   */
  private refreshQuestLive(): void {
    for (const entry of this.questLive) {
      setLive(entry.text, entry.bar, this.goalProgress(entry.goal), entry.goal.n);
    }
    const state = store.fleet;
    if (!this.fleetLive || !state) return;
    const order = fleetOrder(state.order);
    if (!order) return;
    const target = order.goal.n;
    const text = fleetLine(state.progress, target, state.buffLeft);
    if (this.fleetLive.text.textContent !== text) this.fleetLive.text.textContent = text;
    setBar(this.fleetLive.bar, state.progress, target);
  }

  /**
   * Die Nebenaufträge — Ketten von Orten statt Zählern.
   *
   * Sie stehen **über** den Aufträgen, weil sie das sind, was gerade zu tun
   * ist: ein Auftrag läuft nebenbei mit, ein Nebenauftrag verlangt, dass jemand
   * hinfliegt. Gesperrte stehen gedämpft dabei und verschwinden nicht — ein
   * leerer Bereich sieht nach Ende aus, ein Riegel nach Ziel (dieselbe Regel
   * wie beim Technologiebaum).
   */
  private sideHtml(): string {
    const gebaut = builtIds(store.save.built);
    const liste = sideQuests().filter((entry) => entry.minEra <= store.save.era);
    if (!liste.length) return '';

    return '<h3 class="perk-head">Aufträge im Feld</h3>' + liste.map((quest) => {
      const offen = sideUnlocked(store.save, quest, gebaut);
      const done = sideStepsDone(store.save, quest);
      const fertig = sideDone(store.save, quest);
      const geholt = sideClaimed(store.save, quest);
      const reward = Object.entries(quest.reward).map(([res, amount]) => {
        const icon = resource(res);
        return `${icon ? resIconSvg(icon, 14) : ''}${fmt(amount)}`;
      }).join(' ');
      const schritte = quest.steps.map((step, index) => {
        const zustand = index < done ? '✓' : index === done && offen ? '▸' : '·';
        const klasse = index < done ? 'side-done' : index === done ? 'side-next' : 'side-open';
        return `<div class="side-step ${klasse}">${zustand} ${escapeHtml(step.label)}</div>`;
      }).join('');
      const sperre = build(quest.needBuild)?.name ?? quest.needBuild;
      return `<div class="quest ${geholt ? 'done' : fertig ? 'claimable' : ''}"
        style="${offen ? '' : 'opacity:.55'}">
        <div class="quest-top">
          <span class="quest-name">${escapeHtml(quest.name)}${geholt ? ' ✓' : ''}</span>
          <span class="quest-reward">${reward}</span>
        </div>
        <div class="quest-sub">${escapeHtml(quest.desc)}</div>
        ${offen ? schritte : `<div class="quest-sub">Braucht erst: ${escapeHtml(sperre)}</div>`}
        <div class="quest-bar"><div style="width:${(done / quest.steps.length) * 100}%"></div></div>
        ${fertig && !geholt ? `<button data-side="${quest.id}">Belohnung abholen</button>` : ''}
        ${offen && !fertig ? `<button class="dim" data-course="${quest.id}">Kurs setzen</button>` : ''}
      </div>`;
    }).join('');
  }

  /**
   * Der Flottenauftrag der Woche — nur online, das Feld hat das Ziel.
   *
   * Fortschritt und Buff-Restzeit kommen vom Server (`fleet`-Nachricht);
   * Name, Ziel und Wirkung stehen in der Balancing-Tabelle.
   */
  private fleetHtml(): string {
    const state = store.fleet;
    const order = state ? fleetOrder(state.order) : undefined;
    if (!state || !order) return '';
    const target = order.goal.n;
    const done = state.progress >= target;
    return `<div class="quest fleet ${done ? 'done' : ''}">
      <div class="quest-top">
        <span class="quest-name">⚑ Flottenauftrag: ${escapeHtml(order.name)}${done ? ' ✓' : ''}</span>
      </div>
      <div class="quest-sub">${escapeHtml(order.desc)}</div>
      <div class="quest-sub" data-fleet-prog>${
        escapeHtml(fleetLine(state.progress, target, state.buffLeft))}</div>
      <div class="quest-bar"><div data-fleet-bar style="width:${
        clamp(state.progress / target, 0, 1) * 100}%"></div></div>
    </div>`;
  }

  private questProgress(id: string): number {
    const quest = quests().find((entry) => entry.id === id);
    return quest ? this.goalProgress(quest.goal) : 0;
  }

  /** Fortschritt gegen ein Ziel — **eine** Auswertung für Aufträge und Log. */
  private goalProgress(goal: Goal): number {
    switch (goal.type) {
      case 'rocks': return store.stat('rocks');
      case 'res': return store.save.totalRes[goal.id ?? ''] ?? 0;
      case 'level': return store.save.ship.level;
      case 'build': return store.save.built;
      case 'depth': return Math.round(store.stat('deepest'));
      case 'upgrades': return store.upgradeTotal;
      case 'enemies': return store.stat('enemies');
      // Der einzige Zieltyp, der keinen Zähler liest, sondern **wo** der
      // Spieler steht. Wortgleich zu `rules.quest_progress`.
      case 'era': return store.save.era;
      default: return 0;
    }
  }

  // ── Booster ────────────────────────────────────────────────────────────────
  private renderBoosters(): void {
    // Zwei Bits je Booster: läuft er, ist er bezahlbar. Der Kristallstand war
    // derselbe unnötig feine Fall wie in der Ausbauliste, und `ceil(Restzeit)`
    // baute die Knopfzeile jede Sekunde neu mit. Der Countdown wird jetzt an Ort
    // und Stelle geschrieben — siehe `refreshBoosterLive` (Issue #80).
    const key = boosterListKey(
      store.save.res, (id) => store.boosterLeft(id),
      store.save.boosterStacking, store.save.boosterStacks,
    );
    if (key === this.boosterKey) return;
    this.boosterKey = key;

    const currency = resource(balancing().boosterCurrency);
    const stapeln = store.save.boosterStacking;
    // **Der Schalter steht über der Liste**, nicht in jeder Zeile: er gilt für
    // alle. Er sagt, was gleich passiert, nicht was er heißt — und die Höhe
    // kommt aus der Tabelle, nicht als getippte Zahl in den Satz.
    const kopf = `<label class="boostmode">
      <input type="checkbox" data-boostmode ${stapeln ? 'checked' : ''}>
      <span>Stapeln statt verlängern — zünden legt nach, bis ${
        balancing().boosterMaxStack}fach, statt die Zeit zu strecken</span>
    </label>`;

    this.boosterList.innerHTML = kopf + boosters().map((entry) => {
      const left = store.boosterLeft(entry.id);
      const hoehe = Math.max(1, store.save.boosterStacks[entry.id] ?? 1);
      const voll = stapeln && left > 0 && hoehe >= balancing().boosterMaxStack;
      const affordable = (store.save.res[balancing().boosterCurrency] ?? 0) >= entry.cost
        && !voll;
      return `<div class="booster">
        <img class="bi" src="" data-icon="${entry.icon}" alt="" width="34" height="34">
        <div class="btxt">
          <div class="bname">${escapeHtml(entry.name)}${
            left > 0 && hoehe > 1 ? ` <b>×${hoehe}</b>` : ''}<span data-boost-left="${entry.id}">${
            left > 0 ? ` — aktiv ${timeStr(left)}` : ''}</span></div>
          <div class="bdesc">${escapeHtml(entry.desc)} · ${entry.dur}s</div>
        </div>
        <button data-boost="${entry.id}" ${affordable ? '' : 'disabled'}>
          ${currency ? resIconSvg(currency, 13) : ''} ${entry.cost}
        </button>
      </div>`;
    }).join('');

    this.paintIcons(this.boosterList);
    this.boosterList.querySelectorAll<HTMLButtonElement>('[data-boost]').forEach((button) => {
      button.onclick = () => this.actions.buyBooster(button.dataset.boost ?? '');
    });
    const schalter = this.boosterList.querySelector<HTMLInputElement>('[data-boostmode]');
    if (schalter) {
      // Ein **gesetzter** Wert, kein Umschalten: eine verlorene Antwort ließe
      // Haken und Spielstand sonst auseinanderlaufen.
      schalter.onchange = () => this.actions.boosterMode(schalter.checked);
    }
    this.boosterLive = [...this.boosterList.querySelectorAll<HTMLElement>('[data-boost-left]')]
      .map((node) => ({ id: node.dataset.boostLeft ?? '', node }));
  }

  /**
   * Der laufende Countdown — **ohne** Neubau der Knopfzeile.
   *
   * Vorher stand `ceil(Restzeit)` im Änderungsschlüssel: die Liste zeigt den
   * Countdown, ein Neubau je Sekunde war dort also die Aufgabe und nicht der
   * Fehler — aber er nahm die Knöpfe mit, und damit stand das Klickfenster
   * einmal pro Sekunde offen. Derselbe Gedanke wie bei `setMoveBusy`: was jeden
   * Neubau überleben soll, hängt nicht am `innerHTML`.
   *
   * Geschrieben wird nur bei echter Änderung. Ein `textContent`-Vergleich ist
   * billiger als ein Layout, das der Browser sonst je Bild neu rechnet.
   */
  private refreshBoosterLive(): void {
    for (const entry of this.boosterLive) {
      const left = store.boosterLeft(entry.id);
      const text = left > 0 ? ` — aktiv ${timeStr(left)}` : '';
      if (entry.node.textContent !== text) entry.node.textContent = text;
    }
  }

  /** Icons als Datenbild einsetzen — ohne Netz und ohne Bilddatei. */
  private paintIcons(root: HTMLElement): void {
    root.querySelectorAll<HTMLImageElement>('img[data-icon]').forEach((img) => {
      const canvas = document.createElement('canvas');
      canvas.width = 34; canvas.height = 34;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      drawIcon(ctx, img.dataset.icon ?? 'lock', 1, 1, 32);
      img.src = canvas.toDataURL();
    });
  }

  // ── Legende des Technologiebaums ───────────────────────────────────────────
  //
  // Vier Farben, vier Zahlen. Die Farben stehen in derselben Tabelle wie die
  // Kacheln (`gfx/palette`), die Zahlen kommen aus derselben Auswertung wie
  // ihre Farbe (`sim/tree`) — eine zweite Regelquelle zaehlte drei kaufbare
  // Knoten, waehrend der Baum zwei zeigt.
  //
  // Die Zahl ist der eigentliche Gewinn: „kaufbar 0" beantwortet die Frage
  // „habe ich etwas uebersehen?", ohne dass jemand 47 Kacheln absucht.

  private renderTreeLegend(): void {
    const counts = countStates(nodes(), treeStatus(store.save, store.statCard('enemiesByKind')));
    const key = NODE_STATE_ORDER.map((state) => counts[state]).join('|');
    if (key === this.legendKey) return;
    this.legendKey = key;

    this.treeLegend.innerHTML = NODE_STATE_ORDER.map((state) => {
      const style = NODE_STYLE[state];
      return `<span class="leg"><i style="background:${cssColor(style.fill)};` +
        `border-color:${cssColor(style.accent)}"></i>` +
        `${escapeHtml(style.label)} <b>${counts[state]}</b></span>`;
    }).join('');
  }

  // ── Fuß der Karte ──────────────────────────────────────────────────────────
  //
  // `#map-legend` war ein Platzhalter mit CSS und ohne Schreiber — genau dort,
  // wo die Antwort hingehoert. Ohne sie sah die Karte aus wie eine Karte, auf
  // der man tippen kann: der Hinweis darunter sagte „Auf eine Marke tippen
  // setzt den Kurs", und die Ablehnung kam erst **nach** dem Tippen, als
  // Meldung am oberen Bildschirmrand — hinter dem Panel.
  //
  // Der Text kommt aus derselben Auswertung wie die Sperre (`courseLock`),
  // wie Baumfarbe und Legende aus `nodeState`.

  private renderMapFoot(): void {
    const grund = courseLock(store.stats, builtIds(store.save.built));
    const reichweite = Number(store.stats.values.autoRange);
    const key = grund + '#' + Math.round(reichweite);
    if (key === this.mapFootKey) return;
    this.mapFootKey = key;

    this.mapLegend.innerHTML = grund
      ? `<span class="leg-lock">${escapeHtml(grund)}</span>`
      : `<span class="leg">Kursreichweite <b>${escapeHtml(distStr(reichweite))}</b></span>`;
  }

  // ── Tooltip des Technologiebaums ───────────────────────────────────────────
  showNodeTooltip(node: TreeNode | null, x: number, y: number): void {
    if (!node) { hideTooltip(); return; }

    const level = store.upgradeLevel(node.id);
    const built = builtIds(store.save.built);
    const locked = missingReasons(
      node, store.save.levels, built, store.save.ship.level,
      store.statCard('enemiesByKind'),
    );
    const maxed = level >= node.max;
    const cost = nodeCost(node, level);
    const costRes = resource(node.cost.res);
    const affordable = (store.save.res[node.cost.res] ?? 0) >= cost;

    let html = `<div class="tt-name">${escapeHtml(node.name)} (${level}/${node.max})</div>`;
    html += `<div class="tt-desc">${escapeHtml(node.desc)}</div>`;
    const effect = effectPreview(node, store.save.levels);
    if (maxed) html += '<div class="tt-max">maximal ausgebaut</div>';
    else if (effect) html += `<div class="tt-eff">${effect}</div>`;

    if (locked.length) {
      html += `<div class="tt-lock">benötigt: ${escapeHtml(locked.join(', '))}</div>`;
    } else if (!maxed) {
      html += `<div class="tt-cost${affordable ? '' : ' no'}">
        ${costRes ? resIconSvg(costRes, 14) : ''} ${fmt(cost)}</div>`;
    }

    showTooltip(html, x, y);
  }

  // ── Dialoge ────────────────────────────────────────────────────────────────
  /** Ein Modal **pausiert das Spiel**: sonst laeuft die Welt weiter, ohne dass
   *  jemand zusieht — und beim Schliessen ist unerklaerlich, was passiert ist. */
  showModal(title: string, body: string, actions: ModalAction[]): void {
    el('modal-title').textContent = title;
    el('modal-body').innerHTML = body;
    const box = el('modal-actions');
    box.innerHTML = '';
    for (const action of actions) {
      const button = document.createElement('button');
      button.textContent = action.label;
      if (action.dim) button.className = 'dim';
      button.onclick = () => {
        action.submit?.(el('modal-body'));
        this.hideModal();
        action.fn?.();
      };
      box.appendChild(button);
    }
    this.modal.classList.remove('hidden');
    this.actions.setPaused(true);
  }

  hideModal(): void {
    this.modal.classList.add('hidden');
    this.actions.setPaused(false);
  }

  get modalOpen(): boolean {
    return !this.modal.classList.contains('hidden');
  }

  private confirmWipe(): void {
    this.showModal('Neu anfangen?', 'Der gesamte Fortschritt geht verloren.', [
      { label: 'Abbrechen', dim: true },
      { label: 'Alles löschen', fn: () => this.actions.wipe() },
    ]);
  }
}

function goalText(id: string): string {
  const quest = quests().find((entry) => entry.id === id);
  if (!quest) return '';
  const goal = quest.goal;
  switch (goal.type) {
    case 'rocks': return `${goal.n} Asteroiden abbauen`;
    case 'res': return `${resource(goal.id ?? '')?.name ?? goal.id} sammeln`;
    case 'level': return `Schiffslevel ${goal.n} erreichen`;
    case 'build': return `${goal.n} Basis-Ausbaustufen fertigstellen`;
    case 'depth': return `${goal.n} m Abstand zur Basis erreichen`;
    case 'upgrades': return `${goal.n} Upgrade-Stufen kaufen`;
    default: return '';
  }
}

export { booster, goalText };
